// src/components/SheetAccessAccounts.jsx
//
// A campaign's tracker is a Google Sheet. It is shared with the advisor's
// SiteSift sign-in address, which is often an Outlook or a work address that
// Google does not know — and then the Sheet button lands on "You need access".
// This is the one place an advisor lists the other Google accounts they own.
//
// Every change saves immediately through the callable, so a sheet created a
// minute later already opens for the accounts listed here, and the sheets that
// already exist are re-shared by the same call.
import React, { useCallback, useEffect, useId, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { MdClose } from 'react-icons/md';
import { functions } from '../firebase';
import '../styles/SheetAccessAccounts.css';

export const MAX_SHEET_ACCESS_ACCOUNTS = 5;

const TITLE = 'Google accounts that can open your sheets';
const ADD_LABEL = 'Add a Google account';
const HELPER = 'Your campaign sheets are Google Sheets. Add the Google accounts you own '
  + '(Gmail or Google Workspace) so the Sheet button opens them. Your sign-in address is always included.';
const MAX_NOTICE = `You can add up to ${MAX_SHEET_ACCESS_ACCOUNTS} accounts.`;
const LOADING_MESSAGE = 'Loading your accounts…';
const LOAD_FAILED_MESSAGE = 'Could not load your accounts. Your sign-in address still opens your sheets.';
const SHAPE_MESSAGE = 'Enter a complete email address, for example name@gmail.com.';

// Deliberately the same shape the callable enforces, so an address that cannot
// possibly work never costs a round trip.
const EMAIL_SHAPE = /^[^\s@,<>"]+@[^\s@,<>".]+(\.[^\s@,<>".]+)+$/;

function normalize(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function sanitizeList(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const emails = [];
  for (const entry of value) {
    const email = normalize(entry);
    if (!email || seen.has(email)) continue;
    seen.add(email);
    emails.push(email);
  }
  return emails;
}

function callSheetShareEmails(payload) {
  return httpsCallable(functions, 'updateSheetShareEmails')(payload);
}

export default function SheetAccessAccounts({
  signInEmail = '',
  onEmailsChange,
  variant = 'section',
  title = TITLE,
  addLabel = ADD_LABEL,
}) {
  const [accounts, setAccounts] = useState([]);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const inputId = useId();

  // Read the advisor's own list. A failure here is never fatal: the sign-in
  // address always opens the sheets, so the screen degrades to that plus a
  // retry rather than trapping anyone.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadFailed(false);
    callSheetShareEmails({ action: 'get' })
      .then(response => {
        if (cancelled) return;
        setAccounts(sanitizeList(response?.data?.emails));
      })
      .catch(loadError => {
        if (cancelled) return;
        console.warn('Could not load sheet access accounts:', loadError?.message || loadError);
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [reloadToken]);

  const ownAddress = normalize(signInEmail);

  const save = useCallback(async (nextEmails) => {
    setIsSaving(true);
    setError('');
    setResults([]);
    try {
      const response = await callSheetShareEmails({ action: 'set', emails: nextEmails });
      const saved = sanitizeList(response?.data?.emails);
      const rows = Array.isArray(response?.data?.results) ? response.data.results : [];
      setAccounts(saved);
      setResults(rows.filter(row => row && row.message));
      if (typeof onEmailsChange === 'function') onEmailsChange(saved);
      return saved;
    } catch (callableError) {
      setError(callableError?.message || 'Could not update sheet access. Try again in a moment.');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [onEmailsChange]);

  const handleAdd = useCallback(() => {
    const email = normalize(draft);
    if (!EMAIL_SHAPE.test(email)) {
      setResults([]);
      setError(SHAPE_MESSAGE);
      return;
    }
    if (email === ownAddress) {
      setResults([]);
      setError('Your sign-in address already opens your sheets.');
      return;
    }
    if (accounts.includes(email)) {
      setResults([]);
      setError('That account is already on the list.');
      return;
    }
    setDraft('');
    save([...accounts, email]);
  }, [accounts, draft, ownAddress, save]);

  const handleRemove = useCallback((email) => {
    save(accounts.filter(entry => entry !== email));
  }, [accounts, save]);

  const isFull = accounts.length >= MAX_SHEET_ACCESS_ACCOUNTS;
  const controlsDisabled = isSaving || isLoading || isFull;

  return (
    <div
      className={`sheet-access sheet-access--${variant}`}
      aria-busy={isSaving || isLoading}
    >
      <h3 className="sheet-access__title">{title}</h3>
      <p className="sheet-access__helper">{HELPER}</p>

      {isLoading && <p className="sheet-access__status">{LOADING_MESSAGE}</p>}

      {loadFailed && !isLoading && (
        <p className="sheet-access__status sheet-access__status--failed" role="status">
          {LOAD_FAILED_MESSAGE}
          <button
            type="button"
            className="sheet-access__retry"
            onClick={() => setReloadToken(token => token + 1)}
          >
            Retry
          </button>
        </p>
      )}

      <ul className="sheet-access__list">
        <li className="sheet-access__item sheet-access__item--signin">
          <span className="sheet-access__email">{signInEmail}</span>
          <span className="sheet-access__badge">Sign-in address</span>
        </li>
        {accounts.map(email => (
          <li key={email} className="sheet-access__item">
            <span className="sheet-access__email">{email}</span>
            <button
              type="button"
              className="sheet-access__remove"
              aria-label={`Remove ${email}`}
              onClick={() => handleRemove(email)}
              disabled={isSaving}
            >
              <MdClose size={14} aria-hidden="true" />
              Remove
            </button>
          </li>
        ))}
      </ul>

      <div className="sheet-access__add" role="group">
        {addLabel !== ADD_LABEL && (
          <label className="sheet-access__add-label" htmlFor={inputId}>{addLabel}</label>
        )}
        <input
          id={inputId}
          type="email"
          className="sheet-access__input"
          aria-label={addLabel}
          placeholder="name@gmail.com"
          value={draft}
          disabled={controlsDisabled}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <button
          type="button"
          className="sheet-access__add-button"
          onClick={handleAdd}
          disabled={controlsDisabled}
        >
          {isSaving ? 'Saving…' : 'Add'}
        </button>
      </div>

      <p className="sheet-access__max">{MAX_NOTICE}</p>

      {error && (
        <p className="sheet-access__error" role="alert">{error}</p>
      )}

      {results.map(row => (
        <p
          key={`${row.email}-${row.status}`}
          className={`sheet-access__result sheet-access__result--${row.status}`}
          role="alert"
        >
          <span className="sheet-access__result-email">{row.email}</span>
          <span className="sheet-access__result-message">{row.message}</span>
        </p>
      ))}
    </div>
  );
}
